import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    CurrentProject,
    DocumentData,
    FetchProjectResult,
    ProjectState,
    Reference,
    TextSuggestion,
    VideoState,
} from '../../shared/types/projectTypes';

import {
    createProject,
    updateProject,
    uploadDocument,
    fetchProject,
    getRecentProjects,
    exportProject,
    generateContent,
    refineText,
    generateTextSuggestion,
    searchReference,
    importDocument,
    deleteProject,
    createMultiplePosts,
    shareProject,
    editPost,
} from '../../shared/api/textProjectApi';

import {
    GeneratedContent,
    ImageProjectResponse,
    RecentProjectsAttributes,
} from '../../shared/types/imagesType';
import {
    createImageProject,
    updateImageUrl,
    updateLayoutImage,
} from '../../shared/api/imageProjectApi';
import { ScriptData } from '../../shared/types/scriptTypes';
import {
    attachVideoToProject,
    detachVideoFromProject,
    requestVideoPresign,
    uploadVideoToStorage,
} from '../../shared/api/videoProjectApi';
export const initialState: ProjectState = {
    currentProject: null,
    loading: false,
    error: null,
    scriptData: null,
    imageData: null,
    isLoadingSharing: false,
    recentProjects: [],
    isCreatingProject: false,
    refinedText: '',
    isRefining: false,
    textSuggestions: null,
    isTextSuggesting: false,
    references: null,
    isSearchingReferences: false,
    hasNewProject: false,
    postSuccess: false,
    selectedSocialMedia: null,
    videoData: null,
    videoState: {
        isRequesting: false,
        isUploading: false,
        progress: 0,
        lastError: undefined,
        lastFileUrl: undefined,
        presign: undefined,
        recordId: undefined,
        isAttaching: false,
    },
    generatonMode: 'text_only',
    inputPrompt: '',
    multiplePosts: [],
    postsToPublish: [],
};
const projectSlice = createSlice({
    name: 'project',
    initialState,
    reducers: {
        setCurrentProject(state, action: PayloadAction<ProjectState['currentProject']>) {
            state.currentProject = action.payload;
        },
        setImageData(state, action: PayloadAction<ProjectState['imageData']>) {
            state.imageData = action.payload;
        },
        setScriptData(state, action: PayloadAction<ProjectState['scriptData']>) {
            state.scriptData = action.payload;
        },
        setRefinedText(state, action: PayloadAction<string>) {
            state.refinedText = action.payload;
        },
        setPostSuccess(state, action: PayloadAction<boolean>) {
            state.postSuccess = action.payload;
        },
        setSelectedSocialMedia(state, action: PayloadAction<string | null>) {
            state.selectedSocialMedia = action.payload;
        },
        setReferences(state, action: PayloadAction<Reference[]>) {
            state.references = action.payload;
        },
        setVideoUploadProgress(state, action: PayloadAction<number>) {
            state.videoState.progress = Math.max(0, Math.min(100, action.payload));
        },
        resetVideoState(state) {
            state.videoState = {
                isRequesting: false,
                isUploading: false,
                progress: 0,
                lastError: undefined,
                lastFileUrl: undefined,
                presign: undefined,
                recordId: undefined,
            } as VideoState;
        },
        setVideoData(state, action: PayloadAction<ProjectState['videoData']>) {
            state.videoData = action.payload;
        },
        setGenerationMode(
            state,
            action: PayloadAction<'text_image' | 'text_only' | 'image_only' | null>,
        ) {
            state.generatonMode = action.payload;
        },
        setInputPrompt(state, action: PayloadAction<string>) {
            state.inputPrompt = action.payload;
        },
        setMultiplePosts(state, action: PayloadAction<any[]>) {
            state.multiplePosts = action.payload;
        },
        setPostsToPublish(state, action: PayloadAction<any[]>) {
            state.postsToPublish = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createProject.pending, (state) => {
                state.isCreatingProject = true;
                state.error = null;
            })
            .addCase(createProject.fulfilled, (state, action: PayloadAction<CurrentProject>) => {
                state.isCreatingProject = false;
                state.hasNewProject = true;
                state.currentProject = action.payload;
            })
            .addCase(createProject.rejected, (state, action) => {
                state.isCreatingProject = false;
                state.error = action.payload as string;
            })
            .addCase(updateProject.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                updateProject.fulfilled,
                (state, action: PayloadAction<{ project: CurrentProject; script: ScriptData }>) => {
                    state.loading = false;
                    state.hasNewProject = true;
                    state.currentProject = action.payload.project;
                    if (action.payload.script) {
                        state.scriptData = action.payload.script;
                    }
                },
            )
            .addCase(updateProject.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(uploadDocument.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                uploadDocument.fulfilled,
                (state, action: PayloadAction<{ script: ScriptData }>) => {
                    state.loading = false;
                    state.scriptData = action.payload.script;
                },
            )
            .addCase(uploadDocument.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Unknown error occurred';
            })
            .addCase(importDocument.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                importDocument.fulfilled,
                (state, action: PayloadAction<{ script: ScriptData }>) => {
                    state.loading = false;
                    state.scriptData = action.payload.script;
                },
            )
            .addCase(importDocument.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Unknown error occurred';
            })
            .addCase(fetchProject.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProject.fulfilled, (state, action: PayloadAction<FetchProjectResult>) => {
                state.loading = false;
                state.currentProject = action.payload.project;
                if (action.payload.script) {
                    state.scriptData = action.payload.script;
                }
                if (action.payload.image) {
                    state.imageData = {
                        id: action.payload.image.id,
                        url: action.payload.image.url,
                    };
                }
                if (action.payload.video) {
                    state.videoData = {
                        id: action.payload.video.id,
                        url: action.payload.video.url,
                        filename: action.payload.video.filename,
                    };
                }
            })
            .addCase(fetchProject.rejected, (state) => {
                state.loading = false;
            })
            .addCase(generateContent.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                generateContent.fulfilled,
                (state, action: PayloadAction<GeneratedContent>) => {
                    state.loading = false;
                    if (action.payload.scriptData) {
                        state.scriptData = action.payload.scriptData;
                    }
                    if (action.payload.imageData) {
                        state.imageData = action.payload.imageData;
                    }
                },
            )
            .addCase(generateContent.rejected, (state, action) => {
                state.loading = false;
                state.error = (action.payload as string) || 'Failed to generate script';
            })

            .addCase(exportProject.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(exportProject.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
            })
            .addCase(exportProject.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to export project';
            })
            .addCase(shareProject.pending, (state) => {
                state.isLoadingSharing = true;
                state.error = null;
            })
            .addCase(shareProject.fulfilled, (state) => {
                state.isLoadingSharing = false;
            })
            .addCase(shareProject.rejected, (state, action) => {
                state.isLoadingSharing = false;
                state.error = action.payload || 'Failed to share project';
            })

            .addCase(getRecentProjects.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                getRecentProjects.fulfilled,
                (state, action: PayloadAction<RecentProjectsAttributes[]>) => {
                    state.loading = false;
                    state.hasNewProject = false;
                    state.recentProjects = action.payload;
                },
            )
            .addCase(getRecentProjects.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to get recent projects';
            })
            .addCase(createImageProject.pending, (state) => {
                state.isCreatingProject = true;
                state.error = null;
            })
            .addCase(
                createImageProject.fulfilled,
                (state, action: PayloadAction<ImageProjectResponse['data']>) => {
                    state.isCreatingProject = false;
                },
            )
            .addCase(createImageProject.rejected, (state, action) => {
                state.isCreatingProject = false;
                state.error = action.payload || 'Failed to create image project';
            })
            .addCase(refineText.pending, (state) => {
                state.isRefining = true;
                state.error = null;
            })
            .addCase(refineText.fulfilled, (state, action: PayloadAction<string>) => {
                state.isRefining = false;
                state.refinedText = action.payload; // store the content directly
            })
            .addCase(refineText.rejected, (state, action) => {
                state.isRefining = false;
                state.error = action.payload || 'Failed to refine text';
            })
            .addCase(editPost.pending, (state) => {
                state.isRefining = true;
                state.error = null;
            })
            .addCase(editPost.fulfilled, (state, action: PayloadAction<string>) => {
                state.isRefining = false;
                state.refinedText = action.payload;
            })
            .addCase(editPost.rejected, (state, action) => {
                state.isRefining = false;
                state.error = action.payload || 'Failed to refine text';
            })
            .addCase(generateTextSuggestion.pending, (state) => {
                state.isTextSuggesting = true;
                state.error = null;
            })
            .addCase(
                generateTextSuggestion.fulfilled,
                (state, action: PayloadAction<TextSuggestion[]>) => {
                    state.textSuggestions = action.payload;
                    state.isTextSuggesting = false;
                    state.error = null;
                },
            )
            .addCase(generateTextSuggestion.rejected, (state, action) => {
                state.isTextSuggesting = false;
                state.error = action.payload || 'Failed to generate text suggestions';
            })
            .addCase(searchReference.pending, (state) => {
                state.isSearchingReferences = true;
                state.error = null;
            })
            .addCase(searchReference.fulfilled, (state, action: PayloadAction<Reference[]>) => {
                state.references = action.payload;
                state.isSearchingReferences = false;
                state.error = null;
            })
            .addCase(searchReference.rejected, (state, action) => {
                state.isSearchingReferences = false;
                state.error = action.payload || 'Failed to generate text suggestions';
            })
            .addCase(deleteProject.fulfilled, (state, action) => {
                state.hasNewProject = true;
            })
            .addCase(requestVideoPresign.pending, (s) => {
                s.videoState.isRequesting = true;
                s.videoState.lastError = undefined;
                s.videoState.presign = undefined;
            })
            .addCase(requestVideoPresign.fulfilled, (s, a) => {
                s.videoState.isRequesting = false;

                s.videoState.recordId = a.payload.id;

                s.videoState.presign = {
                    method: 'PUT',
                    url: a.payload.uploadUrl,
                    fileUrl: a.payload.url,
                };
            })
            .addCase(requestVideoPresign.rejected, (s, a) => {
                s.videoState.isRequesting = false;
                s.videoState.lastError = String(a.payload || a.error.message);
            })
            .addCase(uploadVideoToStorage.pending, (s) => {
                s.videoState.isUploading = true;
                s.videoState.lastError = undefined;
                s.videoState.progress = 0;
            })
            .addCase(uploadVideoToStorage.fulfilled, (s, a) => {
                s.videoState.isUploading = false;
                s.videoState.lastFileUrl = a.payload.fileUrl;
                s.videoState.progress = 100;
                s.videoData = {
                    id: s.videoState.recordId || '',
                    url: s.videoState.lastFileUrl,
                    filename: s.videoState.recordId || '',
                };
            })
            .addCase(uploadVideoToStorage.rejected, (s, a) => {
                s.videoState.isUploading = false;
                s.videoState.lastError = String(a.payload || a.error.message);
            })
            .addCase(attachVideoToProject.pending, (s) => {
                s.videoState.isAttaching = true;
                s.videoState.lastError = undefined;
            })
            .addCase(attachVideoToProject.fulfilled, (s) => {
                s.videoState.isAttaching = false;
            })
            .addCase(attachVideoToProject.rejected, (s, a) => {
                s.videoState.isAttaching = false;
                s.videoState.lastError = String(a.payload || a.error.message);
            })
            .addCase(detachVideoFromProject.pending, (s) => {
                s.videoState.lastError = undefined;
            })
            .addCase(detachVideoFromProject.fulfilled, (s) => {
                s.videoData = null;
            })
            .addCase(detachVideoFromProject.rejected, (s, a) => {
                s.videoState.lastError = String(a.payload || a.error.message);
            })
            .addCase(updateImageUrl.fulfilled, (state, action) => {
                const { data } = action.payload;
                const { id, attributes } = data;

                if (state.currentProject?.image?.id === id) {
                    console.log('Updating currentProject image URL:', state.currentProject.id);
                    state.currentProject.image = {
                        ...state.currentProject.image,
                        url: attributes.url || state.currentProject.image.url,
                    };
                }
            })
            .addCase(createMultiplePosts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createMultiplePosts.fulfilled, (state, action) => {
                state.loading = false;
                state.multiplePosts = action.payload;
            })
            .addCase(createMultiplePosts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to create multiple posts';
            })
            .addCase(updateLayoutImage.fulfilled, (state, action) => {
                console.log('updateLayoutImage.fulfilled', action.payload);
                state.imageData = {
                    id: action.payload.imageId,
                    url: action.payload.url,
                };
                // state.recentProjects = state.recentProjects.map((project) => {
                //     if (project.id === action.payload.projectId) {
                //         return {
                //             ...project,
                //             image: {
                //                 ...project.image,
                //                 url: action.payload.url,
                //             },
                //         };
                //     }
                //     return project;
                // });
            });
    },
});
export const {
    setCurrentProject,
    setImageData,
    setScriptData,
    setRefinedText,
    setPostSuccess,
    setSelectedSocialMedia,
    setReferences,
    setVideoUploadProgress,
    resetVideoState,
    setGenerationMode,
    setInputPrompt,
    setVideoData,
    setMultiplePosts,
    setPostsToPublish,
} = projectSlice.actions;
export default projectSlice.reducer;
